import { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import { fqsRequest } from '@ctw/shared/api/fqs/client';
import { type SearchGraphqlResponse, searchQuery } from '@ctw/shared/api/fqs/queries/search';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type {
  ResourceInterface,
  SearchResult,
} from '@ctw/shared/types/graphql/__generated__/graphql';
import { QUERY_KEY_RESOURCE_SEARCH } from '@ctw/shared/utils/query-keys';
import type {
  AllergyIntolerance,
  Condition,
  DiagnosticReport,
  DocumentReference,
  MedicationStatement,
  Observation,
} from 'fhir/r4';

export type PatientRecordSearchResult = SearchResult & {
  resource?:
    | DocumentModel
    | AllergyModel
    | ConditionModel
    | MedicationStatementModel
    | DiagnosticReportModel
    | ObservationModel;
};

export function usePatientRecordSearch(searchTerm: string) {
  return usePatientQuery({
    queryId: QUERY_KEY_RESOURCE_SEARCH,
    queryKey: [searchTerm],
    queryFn: async ({ telemetry, graphqlClient, patient }) => {
      const { data } = await fqsRequest<SearchGraphqlResponse>(
        telemetry,
        graphqlClient,
        searchQuery,
        {
          upid: patient.UPID,
          term: searchTerm,
          first: 500,
        },
      );
      return data.Search.edges.map((result) => {
        let resource: PatientRecordSearchResult['resource'] | ResourceInterface;
        switch (result.node.resourceType) {
          case 'DocumentReference':
            resource = new DocumentModel(result.node.resource as DocumentReference);
            break;
          case 'AllergyIntolerance':
            resource = new AllergyModel(result.node.resource as AllergyIntolerance);
            break;
          case 'Condition':
            resource = new ConditionModel(result.node.resource as Condition);
            break;
          case 'MedicationStatement':
            resource = new MedicationStatementModel(result.node.resource as MedicationStatement);
            break;
          case 'DiagnosticReport':
            resource = new DiagnosticReportModel(result.node.resource as DiagnosticReport);
            break;
          case 'Observation':
            resource = new ObservationModel(result.node.resource as Observation);
            break;
          default:
            resource = result.node.resource;
            break;
        }
        return {
          ...result.node,
          resource,
        } as PatientRecordSearchResult;
      });
    },
    enabled: !!searchTerm,
  });
}
